import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Phone } from 'lucide-react';
import { getPublicPortalSiteContentUrl } from '../cms/bridgeCmsEndpoints';
import { BRIDGE_HIDE_CMS_BADGE } from '../../../siteConfig';
import {
  SITE_EMAIL_DISPLAY,
  SITE_EMAIL_MAILTO,
  SITE_FULL_NAME,
  SITE_LOCATION_DISPLAY,
  SITE_PHONE_DISPLAY,
  SITE_PHONE_TEL,
} from '../sitePlaceholders';
import I3HeroBrand from './I3HeroBrand';
import { BOOK_SESSION_PATH } from '../bookingConfig';
import '../styles/I3Footer.css';

const QUICK_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/services', label: 'Services' },
  { to: '/blog', label: 'Journal' },
  { to: '/contact', label: 'Contact' },
  { to: BOOK_SESSION_PATH, label: 'Book a Session' },
] as const;

const I3Footer: React.FC = () => {
  const [liveFromSql, setLiveFromSql] = useState<boolean | null>(null);

  useEffect(() => {
    const url = getPublicPortalSiteContentUrl();
    if (!url) {
      setLiveFromSql(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(url, { cache: 'no-store' });
        if (cancelled) return;
        if (!res.ok) {
          setLiveFromSql(false);
          return;
        }
        const body = (await res.json()) as { content?: unknown };
        setLiveFromSql(body?.content != null);
      } catch {
        if (!cancelled) setLiveFromSql(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const hideBadge = BRIDGE_HIDE_CMS_BADGE;
  const showLiveBadge = liveFromSql === true && !hideBadge;

  return (
    <footer className="i3-footer i3-footer--mockup">
      <div className="i3-footer-mockup-inner">
        <div className="i3-footer-brand-col">
          <I3HeroBrand variant="onLight" compact />
        </div>

        <div className="i3-footer-links-col">
          <h3 className="i3-footer-col-title">Quick Links</h3>
          <ul className="i3-footer-link-list">
            {QUICK_LINKS.map(({ to, label }) => (
              <li key={`${to}-${label}`}>
                <Link to={to}>{label}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="i3-footer-connect-col">
          <h3 className="i3-footer-col-title">Let&apos;s Connect</h3>
          <ul className="i3-footer-connect-list">
            <li>
              <Mail size={16} strokeWidth={1.75} aria-hidden />
              <a href={SITE_EMAIL_MAILTO}>{SITE_EMAIL_DISPLAY}</a>
            </li>
            <li>
              <Phone size={16} strokeWidth={1.75} aria-hidden />
              <a href={`tel:${SITE_PHONE_TEL}`}>{SITE_PHONE_DISPLAY}</a>
            </li>
            <li>
              <MapPin size={16} strokeWidth={1.75} aria-hidden />
              <span>{SITE_LOCATION_DISPLAY}</span>
            </li>
          </ul>
          <div className="i3-footer-social i3-footer-social--mockup">
            <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
              <span className="i3-social-icon" aria-hidden>
                f
              </span>
            </a>
            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              <span className="i3-social-icon" aria-hidden>
                📷
              </span>
            </a>
          </div>
        </div>
      </div>

      <div className="i3-footer-bottom-bar">
        <p className="i3-footer-copy">
          © {new Date().getFullYear()} {SITE_FULL_NAME}. All rights reserved.
        </p>
        <div className="i3-footer-legal">
          <Link to="/contact">Privacy Policy</Link>
          <span className="i3-footer-sep" aria-hidden>
            |
          </span>
          <Link to="/contact">Terms of Service</Link>
        </div>
      </div>

      {showLiveBadge ? (
        <p className="i3-footer-cms-badge" title="This page is loading site content from P5 SQL (portal_site_content).">
          Content: live (P5 CMS)
        </p>
      ) : null}
    </footer>
  );
};

export default I3Footer;
